import type { TThumbnailSize } from 'helpers/file/file.helpers.types';
import { getFilename } from 'helpers/file/getFilename.helper';
import { orderBy } from 'helpers/native/orderBy.helper';
import { ApiService } from 'services/api/api.service';
// store must be imported before teamHeroApi.service: loading the service
// first runs customBaseQuery -> store -> publicTeamHeroApi.service, which
// reads customBaseQuery's exports before they exist (white page, TDZ error)
import { store } from 'store';
import { teamHeroApi } from 'services/teamHeroApi.service';
import type { IFileResourceData } from 'services/fileResources/fileResources.api.types';
import { type IFileResourceRef, parseFileIri } from './fileBinary.helpers';

const parseOrThrow = (iri: string | null | undefined): IFileResourceRef => {
  const ref = parseFileIri(iri);

  if (!ref) {
    throw new Error(`Not a file resource IRI: ${iri}`);
  }

  return ref;
};

const fetchFileResource = async (
  ref: IFileResourceRef,
  forceRefetch: boolean
): Promise<IFileResourceData> => {
  const query = store.dispatch(
    teamHeroApi.endpoints.getFileResource.initiate(
      { resourcePath: ref.resourcePath },
      { forceRefetch }
    )
  );

  try {
    return await query.unwrap();
  } finally {
    query.unsubscribe();
  }
};

/**
 * The binary behind a file resource's /file endpoint. The endpoint either
 * serves it directly or redirects to object storage, which the browser
 * follows. A thumbnail size that doesn't exist is answered with the
 * original. The blob carries the response's content type.
 */
const fetchBinary = async (
  ref: IFileResourceRef,
  thumbnailSize?: TThumbnailSize
): Promise<Blob> =>
  (await ApiService.Get<Blob>(`${ref.resourcePath}/file`, {
    responseType: 'blob',
    params: thumbnailSize ? { thumbnail: thumbnailSize } : undefined,
  })) as Blob;

/**
 * URL to render a file (or one of its thumbnails) with, e.g. as <img src>:
 * an object URL of its binary. Resolves to '' if the file can't be fetched.
 */
export const getFileDisplayUrl = async (
  iri: string | null | undefined,
  thumbnailSize?: TThumbnailSize
): Promise<string> => {
  try {
    return URL.createObjectURL(
      await fetchBinary(parseOrThrow(iri), thumbnailSize)
    );
  } catch {
    return '';
  }
};

/**
 * Display URLs of several files, sorted by their resources' displayOrder.
 * Files that can't be resolved are left out.
 */
export const getFileDisplayUrlsInDisplayOrder = async (
  iris: string[],
  thumbnailSize?: TThumbnailSize
): Promise<string[]> => {
  const resolved = await Promise.all(
    iris.map(async (iri) => {
      try {
        const ref = parseOrThrow(iri);
        const [resource, blob] = await Promise.all([
          fetchFileResource(ref, false),
          fetchBinary(ref, thumbnailSize),
        ]);

        return {
          url: URL.createObjectURL(blob),
          displayOrder: resource.displayOrder,
        };
      } catch {
        return null;
      }
    })
  );

  return orderBy(
    resolved.filter((entry) => entry !== null),
    ['displayOrder'],
    ['asc']
  ).map(({ url }) => url);
};

export interface IFileBlob {
  blob: Blob;
  fileName: string;
  mimeType: string;
}

/**
 * The binary of a file (or one of its thumbnails), for actions on it
 * (download, preview, PDF build, loading into a form, export). Always
 * refetches the file resource, so the file name and type are current even
 * if the binary was just replaced. Rejects if the file can't be resolved
 * or fetched.
 */
export const getFileBlob = async (
  iri: string | null | undefined,
  thumbnailSize?: TThumbnailSize
): Promise<IFileBlob> => {
  const ref = parseOrThrow(iri);
  const [resource, blob] = await Promise.all([
    fetchFileResource(ref, true),
    fetchBinary(ref, thumbnailSize),
  ]);

  // the response's type is the actual one (a thumbnail's may differ from
  // the original's), the resource's is the fallback
  const mimeType = blob.type || resource.mimeType || '';

  return {
    blob: blob.type === mimeType ? blob : new Blob([blob], { type: mimeType }),
    fileName: getFilename(resource.originalFileName, mimeType),
    mimeType,
  };
};

/**
 * Object URL of a file's binary, for handing it to something that takes a
 * URL (edit forms, PDF layouts). The caller owns the URL. Rejects like
 * getFileBlob.
 */
export const getFileObjectUrl = async (
  iri: string | null | undefined,
  thumbnailSize?: TThumbnailSize
): Promise<string> =>
  URL.createObjectURL((await getFileBlob(iri, thumbnailSize)).blob);
